/**
 * Context uploader component.
 *
 * Form for creating new contexts (text, image, or audio).
 *
 * @module ContextUploader
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileImage, FileAudio, X } from 'lucide-react';

type ContextType = 'text' | 'image' | 'audio';

interface ContextUploaderProps {
  newContext: {
    name: string;
    type: ContextType;
    content: string;
    transcript: string;
  };
  contextFile: File | null;
  uploading: boolean;
  onInputChange: (field: string, value: any) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCancel: () => void;
  onTypeChange: (type: ContextType) => void;
}

export const ContextUploader = ({
  newContext,
  contextFile,
  uploading,
  onInputChange,
  onFileUpload,
  onCancel,
  onTypeChange,
}: ContextUploaderProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-ui">Create New Context</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="contextName" className="font-ui">Context Name</Label>
          <Input
            id="contextName"
            value={newContext.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            placeholder="Enter context name"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="contextType" className="font-ui">Type</Label>
          <Select
            value={newContext.type}
            onValueChange={(value: ContextType) => {
              onTypeChange(value);
            }}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {newContext.type === 'text' ? (
          <div>
            <Label htmlFor="contextContent" className="font-ui">
              Text Content
            </Label>
            <Textarea
              id="contextContent"
              value={newContext.content}
              onChange={(e) => onInputChange('content', e.target.value)}
              placeholder="Enter context text..."
              className="mt-1"
              rows={10}
            />
          </div>
        ) : (
          <div>
            <Label className="font-ui">
              {newContext.type === 'image'
                ? 'Upload Image File'
                : 'Upload Audio File'}
            </Label>
            <div className="mt-2 p-4 border-2 border-dashed border-border rounded-lg">
              <input
                type="file"
                accept={newContext.type === 'image' ? 'image/*' : 'audio/*'}
                onChange={onFileUpload}
                className="hidden"
                id="contextFileUpload"
              />
              <label
                htmlFor="contextFileUpload"
                className="flex flex-col items-center cursor-pointer"
              >
                {newContext.type === 'image' ? (
                  <FileImage className="h-8 w-8 text-muted-foreground mb-2" />
                ) : (
                  <FileAudio className="h-8 w-8 text-muted-foreground mb-2" />
                )}
                <span className="font-ui text-sm text-muted-foreground text-center">
                  {contextFile ? (
                    <>
                      <span className="font-ui text-primary font-medium">
                        {contextFile.name}
                      </span>
                      <br />
                      <span className="font-ui text-xs">
                        {(contextFile.size / 1024).toFixed(2)} KB
                      </span>
                    </>
                  ) : (
                    `Click to upload ${newContext.type} file`
                  )}
                </span>
              </label>
            </div>
            {contextFile && (
              <div className="mt-2 p-2 bg-muted rounded flex items-center gap-2">
                {newContext.type === 'image' ? (
                  <FileImage className="h-4 w-4" />
                ) : (
                  <FileAudio className="h-4 w-4" />
                )}
                <span className="font-ui text-sm flex-1">{contextFile.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onInputChange('file', null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {newContext.type === 'audio' && (
          <div>
            <Label htmlFor="contextTranscript" className="font-ui">
              Transcript (Optional)
            </Label>
            <Textarea
              id="contextTranscript"
              value={newContext.transcript}
              onChange={(e) => onInputChange('transcript', e.target.value)}
              placeholder="Enter audio transcript..."
              className="mt-1"
              rows={10}
            />
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Provide a written transcript of the audio content. This will be
              shared across all questions using this audio context.
            </p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={uploading}
          >
            <span className="font-ui">Cancel</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
